/**
 * The guide cluster: informational pages that feed the ATS checker.
 *
 * Content as data so one definition drives the page, the index card, the
 * schema.org Article block and the internal links — the same reason the FAQ
 * and ATS copy live in modules rather than JSX.
 *
 * ── What is not in here ───────────────────────────────────────────────────
 * No sourceless statistics. The genre is full of them ("75% of resumes are
 * rejected by software", "recruiters spend 6 seconds per resume") and they
 * trace back to nothing checkable. On a site whose promise is "nothing
 * invented — and you can check", borrowing one to win a search result is the
 * most expensive sentence we could write.
 *
 * Where a guide makes a claim about parsing behaviour it is FIRST-HAND: what
 * this product's own extractor does, measured. That is both more honest and
 * more useful than repeating the folklore, because almost nobody else writing
 * on this topic has a parser to describe.
 */

export const GUIDES = [
  {
    slug: "what-is-an-ats",
    title: "What Is an ATS? A Plain Explanation for Job Seekers",
    // Distinct from `title`: this is the <h1>, and it should read like a
    // sentence rather than a search query.
    heading: "What an applicant tracking system actually is",
    description:
      "An applicant tracking system stores and indexes applications so a recruiter can search them. It is not a robot that rejects you. Here is what it really does.",
    lede:
      "Almost everything written about applicant tracking systems describes a gatekeeper that reads your resume and decides your fate. That is not what the software does, and believing it sends you fixing the wrong things.",
    updated: "2026-08-31",
    minutes: 6,
    sections: [
      {
        heading: "The short version",
        body: [
          "An applicant tracking system — an ATS — is the database a company uses to receive and organise job applications. Greenhouse, Lever, Ashby, Workable and Workday are five common ones. When you apply through a company's careers page, you are almost always filling in a form belonging to one of these.",
          "It takes your file, extracts the text, stores it as structured fields, and makes the whole pile searchable. That is the job. It is a filing cabinet with a search box.",
        ],
      },
      {
        heading: "The part people get wrong",
        body: [
          "The widespread belief is that an ATS scores your resume and rejects it before a person sees it. The statistics quoted for this trace back to no primary source, which is why you will not find one on this page.",
          "What usually happens is more ordinary. A recruiter opens the pile of applications and searches it — normally for the skills in the job description they wrote — then reads what comes back. Some systems do offer automated knockout questions (work authorisation, a required certification), and those are genuinely automatic. But the bulk of the filtering is a person running a query.",
          "The distinction matters because it changes what you should do. If a machine were grading your resume, you would want to game the grader. Since a person is searching, you want to be findable in their search.",
        ],
      },
      {
        heading: "Which makes it a vocabulary problem",
        body: [
          "If the posting says “Kubernetes” and your resume says “container orchestration”, you did the work and the search will not surface you. Nothing was rejected. You were never in the result set.",
          "This is the failure worth fixing, and it is fixable in a sentence — you are not acquiring a skill, you are naming one you already have in the words the posting uses.",
        ],
      },
      {
        heading: "What this means in practice",
        body: [
          "Read the posting for its nouns, not its tone. The required skills section is, quite literally, the search a recruiter is most likely to run.",
          "Describe your real work using those nouns where they are accurate. Where they are not accurate, do not add them — a keyword you cannot speak to in an interview is worse than its absence.",
          "Check before you send. A match score against the specific posting tells you which of its terms you already carry and which you do not.",
        ],
      },
    ],
    faq: [
      ["Do all companies use an ATS?", "Most companies past a handful of employees do, because receiving applications by email stops working quickly. Very small companies and some agencies still work from an inbox."],
      ["Can an ATS read my PDF?", "Yes, provided the PDF contains real text. A PDF that is a scan or an exported image contains no text to extract, and will come through empty however good it looks."],
      ["Does applying through a job board differ from a company's site?", "The application usually ends up in the same ATS either way. Applying on the company's own careers page removes a hop and is the version this product's Job Radar watches."],
    ],
  },

  {
    slug: "resume-keywords",
    title: "Resume Keywords: How to Find and Use Them Honestly",
    heading: "Resume keywords, without the stuffing",
    description:
      "Which words a job posting is really searching for, the three states a keyword can be in on your resume, and how to add them without claiming anything untrue.",
    lede:
      "“Add keywords” is the most repeated resume advice there is, and the least specific. Which words, from where, and how many times before it stops being writing and starts being spam?",
    updated: "2026-08-31",
    minutes: 7,
    sections: [
      {
        heading: "A keyword is just a term from the posting",
        body: [
          "There is no secret list. The keywords that matter for a given application are the terms in that specific job description — overwhelmingly the skills, tools and titles it names. A different posting has different ones, which is why a single “ATS-optimised” resume sent everywhere is a contradiction.",
          "Postings usually name them in two tiers, and the tiers are not interchangeable. Required skills are the search a recruiter is most likely to run. Preferred ones are tiebreakers. Missing a required term is a different problem from missing a preferred one, and treating them alike sends you rewriting the wrong line.",
        ],
      },
      {
        heading: "Three states, not two",
        body: [
          "Most tools tell you a term is present or absent. There is a third state that matters more than either: present but thin.",
          "If the posting leans on Terraform in four places and your resume mentions it once, in a comma-separated list at the bottom, a present/absent test passes you. A recruiter reading for depth does not. That is a real gap wearing a passing grade.",
          "This checker reports that state separately — matched, missing, and weakly covered — because the fix is different in each case. Missing means decide whether it is true of you. Weak means you have already done the work and buried it.",
        ],
      },
      {
        heading: "How to add one honestly",
        body: [
          "Find where you actually did the thing, and name it there. If you ran deployments on a cluster, the bullet describing that work is where “Kubernetes” belongs — attached to the outcome you produced with it, not floating in a skills list.",
          "This is why the tailoring in this product only rephrases and reorders what is already in your resume. It will not add a company, a date, a metric or a skill you do not have. If a posting needs something you are missing, you get told — you do not get given it.",
          "The test is simple: could you talk about this line for two minutes in an interview? If not, it should not be on the page, whatever it does to a score.",
        ],
      },
      {
        heading: "What not to do",
        body: [
          "Do not paste the job description into white text at the bottom of your resume. It is an old trick, recruiters know it, and extraction pulls invisible text exactly as well as visible text — so it arrives in the search results and in front of the human reading them.",
          "Do not repeat a term to raise a count. Nothing here is counting occurrences, and prose written for a counter reads badly to the person who eventually gets it.",
          "Do not add a skill you cannot demonstrate. The keyword gets you into the result set; the conversation is what you are actually applying for.",
        ],
      },
    ],
    faq: [
      ["How many keywords should my resume have?", "There is no target number. The useful question is which of this posting's required terms are genuinely true of you and currently unsaid or under-said."],
      ["Should I match the exact wording of the posting?", "Where it is accurate, yes — a search matches strings, not synonyms. Where the posting's word is not what you did, use your own and let the difference be visible."],
      ["Do keywords matter more than experience?", "No. They determine whether your experience is found. A resume that surfaces and disappoints is not better than one that never surfaces, but the reverse is not true either."],
    ],
  },

  {
    slug: "ats-resume-format",
    title: "ATS-Friendly Resume Format: What Actually Breaks Parsing",
    heading: "What actually breaks resume parsing",
    description:
      "Tested against a real parser rather than repeated from folklore: which formatting choices lose text, which are harmless, and what to do about the ones that vary.",
    lede:
      "Formatting advice for resumes is mostly inherited. This page is written from the other side of the problem — we run a parser, and this is what it does with the layouts people actually send.",
    updated: "2026-08-31",
    minutes: 8,
    sections: [
      {
        heading: "The only failure that matters",
        body: [
          "Every parsing problem reduces to one thing: text that is in your document but does not come out of it. Nothing errors when that happens. A resume missing its skills section parses to something that looks perfectly complete, and you are then told you lack skills you listed.",
          "So the useful question about any formatting choice is narrow — does the text survive extraction? Most style advice never asks it.",
        ],
      },
      {
        heading: "Tables: fine here, still a risk elsewhere",
        body: [
          "A great many Word templates put skills in a two-column table. Whether that survives depends entirely on how the parser was written, because in the common Python library for reading .docx files, table cells are not part of the document's paragraphs — you have to go and ask for them separately.",
          "We know this precisely, because our own extractor used to miss them. A resume with skills in a table came through without them, and the score then reported those very skills as missing. That is fixed: tables, headers, footers and text boxes are all read now, in the order they appear.",
          "Which is a reason to be honest rather than a reason to relax. We can tell you what our parser does; we cannot tell you what the one at the company you are applying to does. If a single resume is going to many places, a single-column layout removes the question entirely.",
        ],
      },
      {
        heading: "Headers, footers and text boxes",
        body: [
          "Contact details in the document header are the most costly version of this, because the field that goes missing is how somebody reaches you. As with tables, whether it survives is a property of the parser, not of your file.",
          "Text boxes are the same story and are common in designed templates, where a whole sidebar is one floating box. If the extractor does not go looking in the drawing layer, that sidebar does not exist.",
          "The safe move for anything you cannot afford to lose — your name, your email, your phone number — is to put it in the body of the document, at the top, as ordinary text.",
        ],
      },
      {
        heading: "Columns, images and scans",
        body: [
          "Multi-column layouts usually survive as text but can be read in the wrong order, interleaving two columns line by line into something that parses as nonsense. The words are all there; the meaning is not.",
          "Text inside an image is not text. A logo, a skills chart, a rating of five stars for Python — none of it extracts, because there is nothing to extract. The same is true of a resume that has been scanned or exported as a picture: it looks identical to a person and is empty to a parser.",
          "This is worth checking directly. Open your PDF, select all, copy, and paste into a plain text editor. What you see is roughly what a parser sees. If a section is missing there, it is missing everywhere.",
        ],
      },
      {
        heading: "Section headings and file type",
        body: [
          "Use the ordinary words — Experience, Education, Skills. Parsers and people both look for them, and a heading like “Where I've Made an Impact” has to be inferred rather than recognised.",
          "PDF and .docx are both fine and both widely supported. PDF holds its layout across machines, which is usually the deciding factor. Whichever you send, make sure it is a real text document and not a picture of one.",
        ],
      },
    ],
    faq: [
      ["Is a one-page resume necessary for an ATS?", "Length has no effect on parsing. It affects the person reading, not the software."],
      ["Do fonts and colours matter for parsing?", "Not for extraction — the text comes out the same. They matter for the human, and for printing."],
      ["Should I send a PDF or a Word document?", "Either. PDF keeps the layout stable across machines, which is usually the deciding factor. The thing to avoid is a PDF that is really an image."],
      ["How do I know if my resume parses correctly?", "Copy the text out of it and read what you get. Anything you cannot select and copy is not going to be extracted either."],
    ],
  },
];

export const GUIDE_BY_SLUG = Object.fromEntries(GUIDES.map((g) => [g.slug, g]));
